// src/lib/schemas.ts
import { z } from "zod";
import { brand } from "@/config/brand";
import { getActiveBrand } from "@/config/brands";
import { getCanonicalBase } from "@/config/brands/brand-tech";
import authors from "@/lib/data/authors.json";

// AI Grounding validator for FAQ answers (50-200 words optimal for AEO)
export const faqAnswerValidator = z.string().superRefine((text, ctx) => {
  const words = text.trim().split(/\s+/).length;
  // Critical boundaries for schema validity (Google technical minimum)
  if (words < 5) {
    console.warn(`[SEO Critical] FAQ answer is too short (${words} words). Minimum 5 words required for indexing.`);
  }
  // Optimal boundaries for AI Answer Engine Optimization (AEO) - Recommendations for Perplexity/SGE
  if (words < 50 || words > 200) {
    console.info(`[AEO Recommendation] FAQ answer length (${words} words) is outside optimal 50-200 word range for generative extraction.`);
  }
});

// Active-brand technical/canonical separation (Task C). SITE_URL is now sourced
// from getCanonicalBase() — the SINGLE source of truth for the brand domain
// (tech.canonicalDomain ?? brand.website.url, trailing slash stripped). For the
// default brand this is https://esimsupra.com, so eSIMSUPRA output is unchanged;
// other brands get their own canonical domain across every schema @id/url below.
const activeTech = getActiveBrand().tech;
const SITE_URL = getCanonicalBase();
// Legal name: prefer an explicit (non-TBD) legal entity, else the display name.
const rawLegalName = activeTech?.canonical?.organizationLegalName;
const ORG_LEGAL_NAME =
  rawLegalName && !rawLegalName.startsWith("TBD") ? rawLegalName : brand.brand.name;

// Organization Schema (site-wide)
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: brand.brand.name,
  legalName: ORG_LEGAL_NAME,
  url: `${SITE_URL}/`,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo.png`,
    width: 250,
    height: 60,
  },
  description: brand.brand.description,
  foundingDate: "2024",
  contactPoint: {
    "@type": "ContactPoint",
    email: brand.contact.email,
    contactType: "customer service",
    availableLanguage: ["English", "Spanish", "French", "German", "Arabic"],
    areaServed: "Worldwide",
  },
  sameAs: [
    brand.social.facebook,
    brand.social.twitter,
    brand.social.instagram,
    brand.social.youtube,
  ].filter(Boolean),
};

// WebSite Schema with SearchAction
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: brand.brand.name,
  description: brand.brand.description,
  publisher: {
    "@id": `${SITE_URL}/#organization`,
  },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/destinations/?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// Service Schema for eSIM offerings
export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "eSIM Data Plan",
  name: `${brand.brand.name} eSIM Plans`,
  description: brand.brand.description,
  provider: {
    "@id": `${SITE_URL}/#organization`,
  },
  areaServed: {
    "@type": "Place",
    name: "Worldwide",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "eSIM Data Plans",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Country eSIM Plan",
        },
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Regional eSIM Plan",
        },
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    ],
  },
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
  },
};

// MobileApplication Schema for app store presence
export const mobileAppSchema = (platform: "iOS" | "Android") => ({
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: brand.brand.name,
  operatingSystem: platform === "iOS" ? "iOS" : "Android",
  applicationCategory: "TravelApplication",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
  // NOTE: Real app store ratings should be fetched from Apple App Store / Google Play APIs.
  // aggregateRating: {
  //   "@type": "AggregateRating",
  //   ratingValue: "4.8",
  //   ratingCount: "150",
  //   bestRating: "5",
  // },
});

// HowTo Schema for Installation Guide
export const getInstallationHowToSchema = (
  steps: { name: string; text: string; image?: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: `How to Install ${brand.brand.name} on Any Device`,
  description:
    `Step-by-step guide to install and activate ${brand.brand.name} on iPhone, Android, and tablets.`,
  image: `${SITE_URL}/assets/og_images/installation-guide.png`,
  totalTime: "PT10M",
  estimatedCost: {
    "@type": "MonetaryAmount",
    currency: "USD",
    value: "0",
  },
  tool: [
    {
      "@type": "HowToTool",
      name: "eSIM-capable smartphone (iPhone XS+, Samsung S20+, Google Pixel 3+)",
    },
    {
      "@type": "HowToTool",
      name: "Internet connection to scan QR code",
    },
  ],
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    url: `${SITE_URL}/installation-guide/#step${i + 1}`,
    itemListElement: [{
      "@type": "HowToDirection",
      text: s.text,
    }],
    ...(s.image && {
      image: {
        "@type": "ImageObject",
        url: s.image.startsWith('http') ? s.image : `${SITE_URL}${s.image}`,
      }
    })
  })),
});

// ItemList Schema for Destinations
export const channelListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: `${brand.brand.name} Destinations`,
  description:
    `Browse eSIM destinations and regional coverage available on ${brand.brand.name}.`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Country eSIM Plans",
      description: "Local data plans for single destinations",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Regional eSIM Plans",
      description: "Multi-country plans for regional travel",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "Global eSIM Plans",
      description: "Worldwide coverage for long trips",
    },
  ],
};

/**
 * Generic HowTo schema builder for any step-by-step page (troubleshooting,
 * activation, setup). Produces a HowTo entity with HowToSteps. Use on any
 * page where the body contains numbered operational steps.
 *
 * Called from blog posts in the `troubleshooting` template and from
 * device-specific install guides. See research/07-niche-content-standards.md.
 */
export const generateHowToSchema = ({
  name,
  description,
  totalTime,
  steps,
  pageUrl,
  imageUrl,
  tools,
}: {
  name: string;
  description: string;
  totalTime?: string;
  steps: { name: string; text: string; image?: string }[];
  pageUrl: string;
  imageUrl?: string;
  tools?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  ...(imageUrl && {
    image: imageUrl.startsWith("http") ? imageUrl : `${SITE_URL}${imageUrl}`,
  }),
  ...(totalTime && { totalTime }),
  ...(tools && tools.length && {
    tool: tools.map((t) => ({ "@type": "HowToTool", name: t })),
  }),
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    url: `${pageUrl}${pageUrl.includes("#") ? "" : "#"}step${i + 1}`,
    itemListElement: [
      { "@type": "HowToDirection", text: s.text },
    ],
    ...(s.image && {
      image: {
        "@type": "ImageObject",
        url: s.image.startsWith("http") ? s.image : `${SITE_URL}${s.image}`,
      },
    }),
  })),
  publisher: { "@id": `${SITE_URL}/#organization` },
});

/**
 * Generic ItemList schema builder for any enumerated hub page. Used on
 * `/best-esims-by-region/`, `/destinations/`, and comparison listings.
 * Pass each item's name, url, and optional description/image.
 */
export const generateItemListSchema = (
  listName: string,
  items: { name: string; url: string; description?: string; image?: string }[],
  description?: string,
) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: listName,
  ...(description && { description }),
  numberOfItems: items.length,
  itemListOrder: "https://schema.org/ItemListOrderAscending",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    url: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    ...(item.description && { description: item.description }),
    ...(item.image && {
      image: item.image.startsWith("http") ? item.image : `${SITE_URL}${item.image}`,
    }),
  })),
  publisher: { "@id": `${SITE_URL}/#organization` },
});

// FAQPage Schema
export const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is an eSIM and how does it work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: `An eSIM is an embedded digital SIM that allows you to activate a mobile data plan without inserting a physical SIM card into your device. Instead of a removable chip, the eSIM is built directly into your phone's hardware. To use it, you simply scan a QR code or enter activation details, and the profile downloads over Wi-Fi. ${brand.brand.name} delivers eSIM profiles instantly via email, so you can get online within minutes of landing at your destination. This technology eliminates the need to hunt for local SIM cards at airports or rely on expensive roaming from your home carrier.`,
      },
    },
    {
      "@type": "Question",
      name: `What devices are compatible with ${brand.brand.name} eSIM plans?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${brand.brand.name} works on all eSIM-capable devices, including iPhone XS and newer, Samsung Galaxy S20 and newer, Google Pixel 3 and newer, iPad Pro, iPad Air, and select Windows laptops with eSIM support. The device must also be carrier-unlocked to use third-party eSIM profiles. You can verify compatibility by checking your device settings for an eSIM or Digital SIM option. For a complete list of supported models and region-specific variations, visit our compatible phones guide.`,
      },
    },
    {
      "@type": "Question",
      name: "Do you offer refunds or plan changes if something goes wrong?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, we offer refunds and plan adjustments when eligible. If your device turns out to be incompatible with eSIM technology, or if you are unable to activate the plan due to a technical issue on our end, contact our 24/7 support team within 14 days of purchase. We will troubleshoot the issue first, and if it cannot be resolved, we will process a refund or help you switch to a different plan. Plans that have already been activated and used are generally not refundable, but we review each case individually.",
      },
    },
    {
      "@type": "Question",
      name: `What data speeds can I expect with ${brand.brand.name} while traveling?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${brand.brand.name} connects you directly to local 4G and 5G networks in your destination country, which means you get the same speeds as local residents. In major cities with strong 5G coverage, download speeds can reach 100–300 Mbps or higher. In rural areas or buildings with thick walls, you may fall back to 4G LTE, which typically provides 10–50 Mbps. Actual speeds depend on the local carrier's infrastructure, network congestion, and your device's band support. Most ${brand.brand.name} plans also include hotspot tethering at no extra cost.`,
      },
    },
  ],
};

// Product Schema for Pricing Page (triggers 5-star rating + price snippets)
export const pricingProductSchema = (
  planName: string,
  priceOrBounds: string | number | { low: number; high: number; count: number },
  url: string,
  description?: string,
  sku?: string,
  image?: string,
) => {
  const isBounds = typeof priceOrBounds === "object";
  const lowPrice = isBounds ? priceOrBounds.low : priceOrBounds;
  const highPrice = isBounds ? priceOrBounds.high : priceOrBounds;
  const offerCount = isBounds ? priceOrBounds.count : 1;

  const offers = isBounds 
    ? {
        "@type": "AggregateOffer",
        url: `${SITE_URL}${url}`,
        priceCurrency: "USD",
        lowPrice: lowPrice.toString(),
        highPrice: highPrice.toString(),
        offerCount: offerCount,
        availability: "https://schema.org/InStock",
        seller: {
          "@id": `${SITE_URL}/#organization`,
        },
      }
    : {
        "@type": "Offer",
        url: `${SITE_URL}${url}`,
        price: lowPrice.toString(),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: {
          "@id": `${SITE_URL}/#organization`,
        },
        // Google merchant-listing eligibility. eSIMs are delivered instantly
        // and electronically (QR by email) — $0, zero-time "shipping".
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "USD" },
          shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "MIN" },
            transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "MIN" },
          },
        },
        // Real policy (see /refund-policy/): refund within 24h if activation
        // fails on an eSIM-ready, carrier-unlocked device. Modeled as a finite
        // 24h window; the merchantReturnLink discloses the activation-failure
        // condition. We intentionally do NOT overstate an unconditional return.
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "US",
          returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 1,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
          merchantReturnLink: `${SITE_URL}/refund-policy/`,
        },
      };

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${brand.brand.name} ${planName} eSIM Plan`,
    image: image || `${SITE_URL}/logo.png`,
    description: description || `Prepaid eSIM data plan with instant activation for ${planName}.`,
    sku: sku || undefined,
    brand: {
      "@type": "Brand",
      name: brand.brand.name,
    },
    // NOTE: Real reviews are collected via Trustpilot AFS.
    // Re-enable aggregateRating only after integrating live review data.
    // aggregateRating: {
    //   "@type": "AggregateRating",
    //   ratingValue: "4.9",
    //   reviewCount: "1248",
    //   bestRating: "5",
    //   worstRating: "4",
    // },
    offers,
  };
};


// FAQPage Schema with publication context
export const generateFaqPageSchema = (faqs: { question: string, answer: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => {
      faqAnswerValidator.parse(faq.answer);
      return {
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer,
        },
      };
    }),
    "publisher": {
      "@id": `${SITE_URL}/#organization`,
    },
    "datePublished": new Date().toISOString().split('T')[0],
  };
};

// VideoObject Schema for tutorial videos (Supports "Key Moments" via hasPart)
export const generateVideoObjectSchema = (
  title: string,
  description: string,
  videoUrl: string,
  thumbnailUrl: string,
  uploadDate: string,
  transcript?: string,
  clips?: { startOffset: number; endOffset: number; name: string }[]
) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: title,
  description,
  thumbnailUrl: thumbnailUrl.startsWith('http') ? thumbnailUrl : `${SITE_URL}${thumbnailUrl}`,
  uploadDate,
  contentUrl: videoUrl,
  embedUrl: videoUrl,
  duration: "PT5M",
  transcript: transcript || undefined,
  ...(clips && {
    hasPart: clips.map((clip) => ({
      "@type": "Clip",
      name: clip.name,
      startOffset: clip.startOffset,
      endOffset: clip.endOffset,
      url: `${videoUrl}#t=${clip.startOffset}`,
    })),
  }),
  publisher: {
    "@id": `${SITE_URL}/#organization`,
  },
});

// Article Schema for Blog Posts
export const articleSchema = (
  title: string,
  description: string,
  imageUrl: string,
  datePublished: string,
  dateModified: string,
  authorSlug: string,
  pageUrl: string,
  aeoAnswer?: string,
) => {
  const author = authors.find(a => 
    a.slug.toLowerCase() === authorSlug.toLowerCase() || 
    a.name.toLowerCase() === authorSlug.toLowerCase() ||
    a.slug.toLowerCase() === authorSlug.replace(/\s+/g, '-').toLowerCase()
  );
  
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: aeoAnswer || description,
    image: imageUrl.startsWith('http') ? imageUrl : `${SITE_URL}${imageUrl}`,
    datePublished,
    dateModified,
    ...(aeoAnswer && {
      mainEntity: {
        "@type": "Question",
        "name": title,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": aeoAnswer
        }
      }
    }),
    author: author ? {
      "@type": "Person",
      name: author.name,
      jobTitle: author.title,
      url: `${SITE_URL}/authors/${author.slug}/`,
      image: `${SITE_URL}${author.avatar}`,
      knowsAbout: author.knowsAbout,
    } : {
      "@type": "Person",
      name: authorSlug || `${brand.brand.name} Team`,
    },
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
  };
};

/**
 * SoftwareApplication Schema for the Roaming Savings Calculator
 * Supports Phase 5: Linkable Assets (The Engineering Advantage)
 */
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": `${brand.brand.name} Roaming Savings Calculator`,
  "operatingSystem": "All",
  "applicationCategory": "TravelApplication, FinanceApplication",
  "browserRequirements": "Requires JavaScript. Works on all modern browsers.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "description": `Calculate international roaming savings by comparing major carriers like AT&T, Verizon, and Vodafone against ${brand.brand.name} travel packages.`,
  "publisher": {
    "@id": `${SITE_URL}/#organization`
  },
  "featureList": [
    "Carrier Benchmark Comparison",
    "Real-time eSIM Plan Matcher",
    "Usage-based Savings Calculation",
    "Shareable Results URLs"
  ]
};

/**
 * ProfilePage Schema for Individual Author Pages
 * Supports Phase 6: E-E-A-T (Author Authority)
 */
export const profilePageSchema = (authorSlug: string) => {
  const author = authors.find(a => a.slug === authorSlug);
  if (!author) return {};

  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": author.name,
      "jobTitle": author.title,
      "description": author.bio,
      "image": `${SITE_URL}${author.avatar}`,
      "sameAs": [
        author.social?.twitter,
        author.social?.linkedin,
        author.social?.website
      ].filter(Boolean),
      "knowsAbout": author.knowsAbout,
      "worksFor": {
        "@type": "Organization",
        "name": author.worksFor.name,
        "sameAs": author.worksFor.sameAs
      }
    }
  };
};

